import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Text, Flex, Input, Button, HStack, SimpleGrid, Grid, GridItem, Stat, StatLabel, StatNumber, Tabs, TabList, TabPanels, Tab, TabPanel, Progress, Badge, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, List, ListItem, Divider, useDisclosure
} from '@chakra-ui/react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from 'recharts';

const MPHSaQualityDashboard = () => {
  const { accessToken } = useContext(AppContext);
  
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
    const [completionData, setCompletionData] = useState([]);
  const [actionRequiredData, setActionRequiredData] = useState(null);
  
  // Case Search Modal State
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  const [searchCaseId, setSearchCaseId] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'date_of_surgery', direction: 'desc' });
  
  const defaultEndDate = new Date().toISOString().split('T')[0];
  const defaultStartDate = '2025-11-20';
  
  const [filters, setFilters] = useState({
    start_date: defaultStartDate,
    end_date: defaultEndDate,
    surgeon: ''
  });

  const fetchData = async (overrides = {}) => {
    if (!accessToken) return;
    
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      const p_start = overrides.start_date !== undefined ? overrides.start_date : filters.start_date;
      const p_end = overrides.end_date !== undefined ? overrides.end_date : filters.end_date;
      const p_surg = overrides.surgeon !== undefined ? overrides.surgeon : filters.surgeon;

      if (p_start) queryParams.append('start_date', p_start);
      if (p_end) queryParams.append('end_date', p_end);
      if (p_surg) queryParams.append('surgeon', p_surg);

      const queryString = queryParams.toString();
      const endpoint = queryString ? `/mph/sa-quality?${queryString}` : '/mph/sa-quality';
      const chartEndpoint = queryString ? `/mph/sa-triage-stats?${queryString}` : '/mph/sa-triage-stats';
      const completionEndpoint = queryString ? `/mph/sa-coding-completion-stats?${queryString}` : '/mph/sa-coding-completion-stats';
      const actionRequiredEndpoint = '/mph/sa-action-required';

      const [response, chartResponse, completionResponse, actionRequiredResponse] = await Promise.all([
        callApi(endpoint, accessToken, { method: 'GET' }),
        callApi(chartEndpoint, accessToken, { method: 'GET' }),
        callApi(completionEndpoint, accessToken, { method: 'GET' }),
        callApi(actionRequiredEndpoint, accessToken, { method: 'GET' })
      ]);
      
      if (response.ok && chartResponse.ok && completionResponse.ok && actionRequiredResponse.ok) {
        const result = await response.json();
        const chartResult = await chartResponse.json();
        const completionResult = await completionResponse.json();
        const actionRequiredResult = await actionRequiredResponse.json();
        
        if (result.status === 'success') {
          setData(result.data);
        } else {
          setError(result.message || 'Failed to fetch data');
        }
        
        if (chartResult.status === 'success') {
          setChartData(chartResult.data.daily_volume);
        }
        
        if (completionResult.status === 'success') {
          setCompletionData(completionResult.data.completion_stats);
        }
        
        if (actionRequiredResult.status === 'success') {
          setActionRequiredData(actionRequiredResult.data);
        }
      } else {
        setError(`API Error: ${response.status} or ${chartResponse.status} or ${completionResponse.status}`);
      }
    } catch (err) {
      console.error("Error fetching SA quality data:", err);
      setError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [accessToken]);

  
  const handleCaseSearch = async (e) => {
    e.preventDefault();
    if (!searchCaseId.trim() || !accessToken) return;
    
    setSearchLoading(true);
    setSearchError(null);
    onSearchOpen();
    
    try {
      const response = await callApi(`/mph/sa-case-summary/${searchCaseId.trim()}`, accessToken, { method: 'GET' });
      const result = await response.json();
      
      if (response.ok && result.status === 'success') {
        setSearchResult(result.data);
      } else {
        setSearchError(result.message || 'Case not found or access denied.');
        setSearchResult(null);
      }
    } catch (err) {
      setSearchError('An error occurred while searching for the case.');
      setSearchResult(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      surgeon: ''
    });
    fetchData({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      surgeon: ''
    });
  };

  const processedData = useMemo(() => {
    const validData = data.filter(item => item.overall_confidence_score !== null && item.overall_confidence_score !== undefined);
    const totalCases = validData.length;
    let totalScore = 0;
    let scoredCasesCount = 0;
    let lowScoreCount = 0;
    
    const surgeonSummaryMap = {};
    const payerSummaryMap = {};

    const scoreBuckets = {
      '1': 0, '2': 0, '3': 0, '4': 0, '5': 0,
      '6': 0, '7': 0, '8': 0, '9': 0, '10': 0
    };

    validData.forEach(item => {
      if (item.overall_confidence_score !== null && item.overall_confidence_score !== undefined) {
        const score = item.overall_confidence_score;
        
        totalScore += score;
        scoredCasesCount++;
        if (score < 8.5) lowScoreCount++;

        const bucketKey = String(Math.round(score));
        if (scoreBuckets[bucketKey] !== undefined) {
          scoreBuckets[bucketKey]++;
        } else if (score < 1) {
          scoreBuckets['1']++;
        } else if (score > 10) {
          scoreBuckets['10']++;
        }
      }

      // Surgeon Summary
      const surgeon = item.referring_provider || 'Unknown';
      if (!surgeonSummaryMap[surgeon]) {
        surgeonSummaryMap[surgeon] = { name: surgeon, total: 0, totalScore: 0, scoredCount: 0, lowScoreCount: 0 };
      }
      surgeonSummaryMap[surgeon].total++;
      if (item.overall_confidence_score !== null && item.overall_confidence_score !== undefined) {
        const score = item.overall_confidence_score;
        surgeonSummaryMap[surgeon].totalScore += score;
        surgeonSummaryMap[surgeon].scoredCount++;
        if (score < 8.5) surgeonSummaryMap[surgeon].lowScoreCount++;
      }

      // Payer Summary
      const payer = item.carrier_name || item.payer_type || 'Unknown';
      if (!payerSummaryMap[payer]) {
        payerSummaryMap[payer] = { name: payer, total: 0, totalScore: 0, scoredCount: 0 };
      }
      payerSummaryMap[payer].total++;
      if (item.overall_confidence_score !== null && item.overall_confidence_score !== undefined) {
        const score = item.overall_confidence_score;
        payerSummaryMap[payer].totalScore += score;
        payerSummaryMap[payer].scoredCount++;
      }
    });

    const surgeonSummary = Object.values(surgeonSummaryMap).map(surg => {
      return {
        ...surg,
        avgScore: surg.scoredCount > 0 ? (surg.totalScore / surg.scoredCount).toFixed(1) : 'N/A',
        passRate: surg.total > 0 ? (((surg.total - surg.lowScoreCount) / surg.total) * 100).toFixed(1) : 0
      };
    }).sort((a, b) => b.total - a.total).slice(0, 15);

    const payerSummary = Object.values(payerSummaryMap).map(payer => {
      return {
        ...payer,
        avgScore: payer.scoredCount > 0 ? (payer.totalScore / payer.scoredCount).toFixed(1) : 'N/A'
      };
    }).sort((a, b) => b.total - a.total).slice(0, 10);

    const avgScore = scoredCasesCount > 0 ? (totalScore / scoredCasesCount).toFixed(1) : 'N/A';
    const lowScorePct = totalCases > 0 ? ((lowScoreCount / totalCases) * 100).toFixed(1) : 0;

    const scoreDistribution = Object.keys(scoreBuckets).map(key => ({
      name: key,
      count: scoreBuckets[key]
    }));

    return {
      kpis: {
        total: totalCases,
        avgScore,
        lowScorePct
      },
      allCases: validData,
      surgeonSummary,
      payerSummary,
      scoreDistribution
    };
  }, [data]);

  
  const formatDateShort = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatReasoning = (text) => {
    if (!text) return 'No issues noted.';
    let formatted = text.replace(/^The AI[:\s]+/i, '');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const exportToCSV = () => {
    if (!processedData.allCases || processedData.allCases.length === 0) return;

    const headers = [
      'Date', 'Case ID', 'Case Description', 'Payer', 'Overall Confidence Score', 'Gut Check Notes'
    ];

    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '""';
      const stringified = String(str);
      if (stringified.includes(',') || stringified.includes('"') || stringified.includes('\n')) {
        return `"${stringified.replace(/"/g, '""')}"`;
      }
      return `"${stringified}"`;
    };

    const csvRows = processedData.allCases.map(item => {
      return [
        escapeCSV(item.date_of_surgery),
        escapeCSV(item.external_case_id),
        escapeCSV(item.case_description),
        escapeCSV(item.carrier_name || item.payer_type),
        escapeCSV(item.overall_confidence_score),
        escapeCSV(item.overall_gut_check_notes || formatReasoning(item.coder_reasoning))
      ];
    });

    const csvContent = [headers.join(','), ...csvRows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `mph_sa_quality_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortedCases = useMemo(() => {
    let sortableItems = [...processedData.allCases];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'date_of_surgery') {
          aValue = new Date(aValue || 0).getTime();
          bValue = new Date(bValue || 0).getTime();
        } else if (sortConfig.key === 'payer') {
          aValue = (a.carrier_name || a.payer_type || '').toLowerCase();
          bValue = (b.carrier_name || b.payer_type || '').toLowerCase();
        } else if (sortConfig.key === 'case_type') {
          aValue = (a.case_type || '').toLowerCase();
          bValue = (b.case_type || '').toLowerCase();
        } else if (sortConfig.key === 'overall_confidence_score') {
          aValue = aValue !== null && aValue !== undefined ? aValue : -1;
          bValue = bValue !== null && bValue !== undefined ? bValue : -1;
        } else {
          aValue = aValue ? String(aValue).toLowerCase() : '';
          bValue = bValue ? String(bValue).toLowerCase() : '';
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [processedData.allCases, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderTable = (casesToRender, title) => (
    <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
      <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200">
        <Heading size="sm" color="gray.800">{title}</Heading>
      </Box>
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th cursor="pointer" onClick={() => requestSort('date_of_surgery')} _hover={{ bg: 'gray.100' }}>DOS{getSortIndicator('date_of_surgery')}</Th>
              <Th cursor="pointer" onClick={() => requestSort('external_case_id')} _hover={{ bg: 'gray.100' }}>Case ID{getSortIndicator('external_case_id')}</Th>
              <Th cursor="pointer" onClick={() => requestSort('case_type')} _hover={{ bg: 'gray.100' }}>Case Type{getSortIndicator('case_type')}</Th>
              <Th width="30%" cursor="pointer" onClick={() => requestSort('case_description')} _hover={{ bg: 'gray.100' }}>Case Description{getSortIndicator('case_description')}</Th>
              <Th cursor="pointer" onClick={() => requestSort('payer')} _hover={{ bg: 'gray.100' }}>Payer{getSortIndicator('payer')}</Th>
              <Th isNumeric cursor="pointer" onClick={() => requestSort('overall_confidence_score')} _hover={{ bg: 'gray.100' }}>Overall Confidence{getSortIndicator('overall_confidence_score')}</Th>
              <Th width="25%">Gut Check Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {casesToRender.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8} color="gray.500">No cases found.</Td>
              </Tr>
            ) : (
              casesToRender.map((item, idx) => (
                <Tr key={idx} _hover={{ bg: "gray.50" }}>
                  <Td whiteSpace="nowrap">{item.date_of_surgery || 'N/A'}</Td>
                  <Td>
                    <Text as="a" href={`/mph-surgical-assist/${item.external_case_id}`} target="_blank" color="blue.500" _hover={{ textDecoration: 'underline' }} title={item.external_case_id}>
                      {item.external_case_id ? (item.external_case_id.length > 7 ? item.external_case_id.substring(0, 7) + '...' : item.external_case_id) : 'N/A'}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={item.case_type === 'MPH Surgical Assist NoOp' ? 'green' : 'blue'} fontSize="0.7em">
                      {item.case_type === 'MPH Surgical Assist NoOp' ? 'No-Op' : 'Standard'}
                    </Badge>
                  </Td>
                  <Td whiteSpace="normal">
                    <Text fontSize="xs" noOfLines={3} title={item.case_description}>
                      {item.case_description || 'N/A'}
                    </Text>
                  </Td>
                  <Td>{item.carrier_name || item.payer_type || 'Unknown'}</Td>
                  <Td isNumeric>
                    <Text fontWeight="bold" color={item.overall_confidence_score < 8.5 ? "red.500" : "green.500"}>
                      {item.overall_confidence_score !== null ? `${item.overall_confidence_score}/10` : 'N/A'}
                    </Text>
                  </Td>
                  <Td whiteSpace="normal">
                    <Text fontSize="xs" noOfLines={3} title={item.overall_gut_check_notes || formatReasoning(item.coder_reasoning)}>
                      {item.overall_gut_check_notes || formatReasoning(item.coder_reasoning)}
                    </Text>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">Surgical Assist Dashboard</Heading>
        </Flex>

        <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6} borderWidth="1px">
          <form onSubmit={handleFilterSubmit}>
            <Flex gap={4} flexWrap="wrap" alignItems="flex-end">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Start Date</Text>
                <Input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} size="sm" />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>End Date</Text>
                <Input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} size="sm" />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Surgeon</Text>
                <Input type="text" name="surgeon" placeholder="Filter by surgeon..." value={filters.surgeon} onChange={handleFilterChange} size="sm" />
              </Box>
              <HStack>
                <Button type="submit" colorScheme="blue" size="sm">Apply Filters</Button>
                <Button onClick={handleClearFilters} variant="outline" size="sm">Clear</Button>
                <Button onClick={exportToCSV} colorScheme="green" size="sm" isDisabled={processedData.allCases.length === 0}>Export CSV</Button>
              </HStack>
              <Box ml="auto" borderLeftWidth="1px" pl={4} borderColor="gray.200">
                <Text fontSize="sm" fontWeight="bold" mb={1}>Case Search</Text>
                <HStack>
                  <Input 
                    type="text" 
                    placeholder="Enter Case ID..." 
                    value={searchCaseId} 
                    onChange={(e) => setSearchCaseId(e.target.value)} 
                    size="sm" 
                    width="200px"
                  />
                  <Button onClick={handleCaseSearch} colorScheme="teal" size="sm">Search</Button>
                </HStack>
              </Box>

            </Flex>
          </form>
        </Box>

        {error && (
          <Box bg="red.50" color="red.600" p={4} borderRadius="md" mb={6}>
            <Text fontWeight="bold">Error loading data:</Text>
            <Text>{error}</Text>
          </Box>
        )}

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
              <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                <Heading size="sm" mb={4} color="gray.600">Score Distribution: {processedData.kpis.total} Cases</Heading>
                <Box height="250px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={processedData.scoreDistribution} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{fontSize: 12}} />
                      <YAxis tick={{fontSize: 12}} />
                      <Tooltip />
                      <ReferenceArea x1="8" x2="10" fill="#38A169" fillOpacity={0.3} />
                      <Bar dataKey="count" fill="#4299E1" name="Cases" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Box></Box>
            </SimpleGrid>

            <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)}>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>For Review</Tab>
                <Tab>No-Op Cases</Tab>
                <Tab>Volume & Completion Charts</Tab>
                <Tab>Action Required</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0} py={4}>
                  {renderTable(sortedCases, `All Cases (${processedData.allCases.length})`)}
                </TabPanel>

                <TabPanel px={0} py={4}>
                  {renderTable(
                    sortedCases.filter(item => item.overall_confidence_score !== null && item.overall_confidence_score <= 8),
                    `For Review (${sortedCases.filter(item => item.overall_confidence_score !== null && item.overall_confidence_score <= 8).length})`
                  )}
                </TabPanel>

                <TabPanel px={0} py={4}>
                  {renderTable(
                    sortedCases.filter(item => item.case_type === 'MPH Surgical Assist NoOp'),
                    `No-Op Cases (${sortedCases.filter(item => item.case_type === 'MPH Surgical Assist NoOp').length})`
                  )}
                </TabPanel>

                <TabPanel px={0} py={4}>

                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                    <Heading size="sm" mb={4} color="gray.600">Case Creation Volume (With vs Without DOS)</Heading>
                    <Box height="400px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={completionData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="date" tick={{fontSize: 12}} />
                          <YAxis tick={{fontSize: 12}} domain={[0, 200]} allowDataOverflow={true} />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          <Bar dataKey="has_dos_count" name="With Date of Procedure" stackId="a" fill="#38A169" />
                          <Bar dataKey="without_dos_count" name="Without Date of Procedure" stackId="a" fill="#3182ce" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                    <Heading size="sm" mb={4} color="gray.600">Coding Status by Date of Procedure</Heading>
                    <Box height="400px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="date" tick={{fontSize: 12}} />
                          <YAxis tick={{fontSize: 12}} />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          <Bar dataKey="coded_count" name="Coded" stackId="a" fill="#38A169" />
                          <Bar dataKey="not_coded_count" name="Not Coded" stackId="a" fill="#3182ce" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                    <Heading size="sm" mb={4} color="gray.600">Case Volume by Date of Procedure</Heading>
                    <Box height="400px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="date" tick={{fontSize: 12}} />
                          <YAxis tick={{fontSize: 12}} />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          <Bar dataKey="with_op" name="With OP Report" stackId="a" fill="#38A169" />
                          <Bar dataKey="without_op" name="Without OP Report" stackId="a" fill="#3182ce" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </TabPanel>

                {/* Payer Volume Tab Panel - Commented out per request
                <TabPanel px={0} py={4}>
                  <SimpleGrid columns={1} spacing={6} mb={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">Volume by Top Payers</Heading>
                      <Box height="400px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.payerSummary} layout="vertical" margin={{ top: 5, right: 30, left: 150, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" tick={{fontSize: 11}} width={140} />
                            <Tooltip />
                            <Bar dataKey="total" fill="#4299E1" name="Total Cases" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                    <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Payer / Carrier</Th>
                            <Th isNumeric>Total Cases</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {processedData.payerSummary.length === 0 ? (
                            <Tr><Td colSpan={2} textAlign="center" py={4}>No data available.</Td></Tr>
                          ) : (
                            processedData.payerSummary.map((payer, index) => (
                              <Tr key={index} _hover={{ bg: "gray.50" }}>
                                <Td fontWeight="medium">{payer.name}</Td>
                                <Td isNumeric>{payer.total}</Td>
                              </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </SimpleGrid>
                </TabPanel>
                */}
              
                <TabPanel px={0} py={4}>
                  {actionRequiredData ? (
                    <SimpleGrid columns={1} spacing={8}>
                      <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
                        <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200" bg="red.50">
                          <Heading size="sm" color="red.800">Missing Date of Procedure ({actionRequiredData.missing_dos.length})</Heading>
                        </Box>
                        <Box overflowX="auto" maxHeight="400px">
                          <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} bg="white" zIndex={1}>
                              <Tr>
                                <Th>Case ID</Th>
                                <Th>Date Created</Th>
                                <Th>Case Description</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {actionRequiredData.missing_dos.length === 0 ? (
                                <Tr><Td colSpan={3} textAlign="center" py={4}>No cases found.</Td></Tr>
                              ) : (
                                actionRequiredData.missing_dos.map((item, idx) => (
                                  <Tr key={idx} _hover={{ bg: "gray.50" }}>
                                    <Td>
                                      <Text as="a" href={`/mph-surgical-assist/${item.external_case_id}`} target="_blank" color="blue.500" _hover={{ textDecoration: 'underline' }}>
                                        {item.external_case_id}
                                      </Text>
                                    </Td>
                                    <Td>{formatDateShort(item.date_created)}</Td>
                                    <Td><Text fontSize="xs" noOfLines={2}>{item.case_description || 'N/A'}</Text></Td>
                                  </Tr>
                                ))
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>

                      <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
                        <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200" bg="orange.50">
                          <Heading size="sm" color="orange.800">Stalled OP Reports (> 2 Days, Not Coded) ({actionRequiredData.stalled_op_reports.length})</Heading>
                        </Box>
                        <Box overflowX="auto" maxHeight="400px">
                          <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} bg="white" zIndex={1}>
                              <Tr>
                                <Th>Case ID</Th>
                                <Th>Date Created</Th>
                                <Th>Date of Surgery</Th>
                                <Th>OP Upload Date</Th>
                                <Th>Case Description</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {actionRequiredData.stalled_op_reports.length === 0 ? (
                                <Tr><Td colSpan={5} textAlign="center" py={4}>No cases found.</Td></Tr>
                              ) : (
                                actionRequiredData.stalled_op_reports.map((item, idx) => (
                                  <Tr key={idx} _hover={{ bg: "gray.50" }}>
                                    <Td>
                                      <Text as="a" href={`/mph-surgical-assist/${item.external_case_id}`} target="_blank" color="blue.500" _hover={{ textDecoration: 'underline' }}>
                                        {item.external_case_id}
                                      </Text>
                                    </Td>
                                    <Td>{formatDateShort(item.date_created)}</Td>
                                    <Td>{formatDateShort(item.dos)}</Td>
                                    <Td>{formatDateShort(item.op_upload_date)}</Td>
                                    <Td><Text fontSize="xs" noOfLines={2}>{item.case_description || 'N/A'}</Text></Td>
                                  </Tr>
                                ))
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>

                      <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
                        <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200" bg="yellow.50">
                          <Heading size="sm" color="yellow.800">Old Uncoded Cases (> 75 Days, DOS >= 7/1/2024) ({actionRequiredData.old_uncoded.length})</Heading>
                        </Box>
                        <Box overflowX="auto" maxHeight="400px">
                          <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} bg="white" zIndex={1}>
                              <Tr>
                                <Th>Case ID</Th>
                                <Th>Date Created</Th>
                                <Th>Date of Surgery</Th>
                                <Th>Case Description</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {actionRequiredData.old_uncoded.length === 0 ? (
                                <Tr><Td colSpan={4} textAlign="center" py={4}>No cases found.</Td></Tr>
                              ) : (
                                actionRequiredData.old_uncoded.map((item, idx) => (
                                  <Tr key={idx} _hover={{ bg: "gray.50" }}>
                                    <Td>
                                      <Text as="a" href={`/mph-surgical-assist/${item.external_case_id}`} target="_blank" color="blue.500" _hover={{ textDecoration: 'underline' }}>
                                        {item.external_case_id}
                                      </Text>
                                    </Td>
                                    <Td>{formatDateShort(item.date_created)}</Td>
                                    <Td>{formatDateShort(item.dos)}</Td>
                                    <Td><Text fontSize="xs" noOfLines={2}>{item.case_description || 'N/A'}</Text></Td>
                                  </Tr>
                                ))
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    </SimpleGrid>
                  ) : (
                    <Flex justify="center" p={10}><Spinner /></Flex>
                  )}
                </TabPanel>

              </TabPanels>
            </Tabs>
          </>
        )}
      </Box>

      {/* Case Search Modal */}
      <Modal isOpen={isSearchOpen} onClose={onSearchClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Case Summary: {searchCaseId}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {searchLoading ? (
              <Flex justify="center" p={6}><Spinner /></Flex>
            ) : searchError ? (
              <Text color="red.500">{searchError}</Text>
            ) : searchResult ? (
              <Box>
                <SimpleGrid columns={2} spacing={4} mb={6}>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Date Created</Text>
                    <Text fontWeight="bold">{searchResult.date_created ? new Date(searchResult.date_created).toLocaleDateString() : 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Extracted DOS</Text>
                    <Text fontWeight="bold">{searchResult.extracted_dos || 'Not Found'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Coding Status</Text>
                    <Badge colorScheme={searchResult.coding_status === 'Fully Coded' ? 'green' : 'yellow'}>
                      {searchResult.coding_status}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Date Coded</Text>
                    <Text fontWeight="bold">{searchResult.date_coded ? new Date(searchResult.date_coded).toLocaleDateString() : 'N/A'}</Text>
                  </Box>
                </SimpleGrid>
                
                <Divider mb={4} />
                
                <Heading size="sm" mb={3}>Uploaded Files ({searchResult.files?.length || 0})</Heading>
                {searchResult.files && searchResult.files.length > 0 ? (
                  <Box maxHeight="300px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    <List spacing={2}>
                      {searchResult.files.map((file, idx) => (
                        <ListItem key={idx} p={2} _hover={{ bg: 'gray.50' }} borderRadius="md">
                          <Flex justify="space-between" align="center">
                            <Box>
                              <Text fontWeight="medium" fontSize="sm">{file.file_name}</Text>
                              <Text fontSize="xs" color="gray.500">
                                Uploaded: {file.created_at ? new Date(file.created_at).toLocaleString() : 'Unknown'}
                              </Text>
                            </Box>
                            {file.ai_triage_category && (
                              <Badge fontSize="0.6em" colorScheme="blue">{file.ai_triage_category}</Badge>
                            )}
                          </Flex>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500">No files uploaded for this case.</Text>
                )}
              </Box>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MPHSaQualityDashboard;
